import { Navigate, RouteObject, useParams } from "react-router-dom";
import { createDevelopmentLotSummaryUrl } from "src/RouteUrls";
import { NotFound } from "src/routes/NotFound";
import { DevelopmentLayout } from "src/routes/developments/DevelopmentLayout";
import {
  DevelopmentBeamSideNavLayout,
  DevelopmentLegacySideNavLayout,
} from "src/routes/developments/DevelopmentSideNavLayout";
import { AppShell } from "src/routing/components/AppShell";
import { BeamNavbarLayout } from "src/routing/components/layouts/BeamNavbarLayout";
import { GlobalNavLayout } from "src/routing/components/layouts/GlobalNavLayout";
import { changeLogBeamSideNavRoutes } from "src/routing/domains/developments/change-log/changeLog";
import { developmentRoute } from "src/routing/domains/developments/detail/developmentDetailPaths";
import { fullPageUnderDevelopmentRoutes } from "src/routing/domains/developments/full-page/fullPage";
import { overviewBeamSideNavRoutes, overviewSideNavRoutes } from "src/routing/domains/developments/overview/overview";
import { planAndOptionsSideNavRoutes } from "src/routing/domains/developments/plan-and-options/planAndOptions";
import {
  procurementBeamSideNavRoutes,
  procurementSideNavRoutes,
} from "src/routing/domains/developments/procurement/procurement";
import { productOfferingsSideNavRoutes } from "src/routing/domains/developments/product-offerings/productOfferings";
import { schedulesSideNavRoutes } from "src/routing/domains/developments/schedules/schedules";
import { settingsSideNavRoutes } from "src/routing/domains/developments/settings/settings";
import { templatesSideNavRoutes } from "src/routing/domains/developments/templates/templates";

/**
 * `/developments/:developmentId/*` detail tree.
 *
 * Role: data router domain route.
 *
 * Detail stacks coexist during migration:
 * - Legacy: `AppShell` → `DevelopmentLayout` → `GlobalNavLayout` → `DevelopmentLegacySideNavLayout` → page.
 * - Beam: `AppShell` → `DevelopmentLayout` → `BeamNavbarLayout` → `DevelopmentBeamSideNavLayout` → page.
 * Full-page flows (edit-options, review-templates, reassign-POs) are siblings under
 * `GlobalNavLayout` with no SideNav and no `ScrollableParentLayout` — they own scroll
 * (`StepperProvider newLayout` or their own `ScrollableParent`).
 *
 * Path constants live in subtree `*Paths.ts` / `developmentsPaths.ts`.
 */

/**
 * "Legacy" refers to pages using the interim `BeamSideNavViewportLayout` which mimics Beam's
 * SideNavLayout, but wraps the page content in the <ScrollableParent> wrapper.
 * This is a stopgap solution while we migrate pages to Beam's SideNavLayout, which does not use
 * the <ScrollableParent> wrapper.
 * Migrated pages are defined in the below RouteObject `developmentBeamDetailChildren`.
 */
const developmentLegacyDetailChildren: RouteObject[] = [
  {
    Component: DevelopmentLegacySideNavLayout,
    children: [
      { index: true, Component: RedirectToDevelopmentLotSummary },
      ...overviewSideNavRoutes,
      ...schedulesSideNavRoutes,
      ...planAndOptionsSideNavRoutes,
      ...productOfferingsSideNavRoutes,
      ...templatesSideNavRoutes,
      ...procurementSideNavRoutes,
      ...settingsSideNavRoutes,
      { path: "*", Component: NotFound },
    ],
  },
  // Full-page development flows — keep `DevelopmentLayout` context, skip SideNav chrome.
  // Do not wrap in `ScrollableParentLayout` (pages own scroll; nesting breaks wizard height).
  ...fullPageUnderDevelopmentRoutes,
];

/** Development leaves that have migrated to Beam's SideNavLayout and document-scroll page layouts. */
const developmentBeamDetailChildren: RouteObject[] = [
  {
    Component: DevelopmentBeamSideNavLayout,
    children: [...overviewBeamSideNavRoutes, ...procurementBeamSideNavRoutes, ...changeLogBeamSideNavRoutes],
  },
];

/**
 * Development detail tree without AppShell/navbar — for unit tests and stories that render
 * `DevelopmentLayout` + SideNav leaves. Pass `appShell: true` to `render` / `setStory` so the
 * test helper owns `path` (same slot as `AppShell` in `developmentDetailMigratedRoute`).
 */
export const developmentDetailRoutes: RouteObject[] = [
  {
    path: developmentRoute.path,
    Component: DevelopmentLayout,
    children: [...developmentBeamDetailChildren, ...developmentLegacyDetailChildren],
  },
];

/** Production: AppShell owns the path → pathless DevelopmentLayout → a chrome wrapper → leaves. */
export const developmentDetailMigratedRoute: RouteObject = {
  path: developmentRoute.path,
  Component: AppShell,
  children: [
    {
      Component: DevelopmentLayout,
      children: [
        { Component: BeamNavbarLayout, children: developmentBeamDetailChildren },
        { Component: GlobalNavLayout, children: developmentLegacyDetailChildren },
      ],
    },
  ],
};

function RedirectToDevelopmentLotSummary() {
  const { developmentId } = useParams<{ developmentId: string }>();
  return <Navigate to={createDevelopmentLotSummaryUrl(developmentId!)} replace />;
}
